import { redirect } from 'react-router-dom';
import z from 'zod';
import { AuthenticationService } from 'src/api/authentication_service';
import { notifyError, notifySuccess } from 'src/components/toast';
import { getPlatform } from 'src/config/environment';
import {
  Ethnicity,
  MetastaticDiagnosisCode,
  NccnRiskCategory,
  NStage,
  PatientStatusAtBiopsy,
  PrimaryDiagnosisCode,
  SpecimenType,
  SurgicalMargin,
  TStage,
} from 'src/types';
import { DistributionPlatform, Region } from 'src/types/env';
import { UserRole } from 'src/types/user_roles';

const DEFAULT_USER_LOCALE = 'en-US';

function isValidLocale(locale: string): boolean {
  try {
    // supportedLocalesOf throws RangeError for syntactically invalid tags
    // (e.g. the literal string "undefined" from some automation contexts).
    return Intl.DateTimeFormat.supportedLocalesOf([locale]).length > 0;
  } catch {
    return false;
  }
}

/**
 * Attempts to determine the user's preferred locale from the browser settings.
 * Falls back to 'en-US' if no locale is found or the browser reports an invalid tag.
 */
export function getUserLocale(): string {
  // Use navigator.languages for a ranked array of user preferences (modern browsers)
  if (navigator.languages && navigator.languages.length > 0) {
    const preferred = navigator.languages[0];
    if (preferred && isValidLocale(preferred)) {
      return preferred;
    }
  }

  // Fall back to the older navigator.language property
  // Note: For full cross-browser compatibility, especially older IE versions,
  // one might also check navigator.userLanguage or navigator.browserLanguage,
  // but these are non-standard and require type declarations.
  if (navigator.language && isValidLocale(navigator.language)) {
    return navigator.language;
  }

  return DEFAULT_USER_LOCALE;
}

export function capitalizeFirstLetter(val: string): string {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

export class SpecimenTypeUtils {
  public static readonly URL_TO_SPECIMEN_TYPE_MAP: Record<
    string,
    SpecimenType
  > = {
    prostate_biopsy: SpecimenType.ProstateBiopsy,
    breast_biopsy: SpecimenType.BreastBiopsy,
    prostate_post_rp: SpecimenType.ProstatePostRp,
    colorectal_cancer: SpecimenType.ColorectalCancer,
    metastatic_prostate_biopsy: SpecimenType.MetastaticProstateBiopsy,
  };

  /** Specimen type metadata - platform agnostic constants */
  private static readonly SPECIMEN_TYPE_METADATA = {
    [SpecimenType.ProstateBiopsy]: {
      displayName: 'Prostate — Biopsy',
      description:
        'Select this option when submitting a needle core biopsy sample for a patient with confirmed diagnosis of prostate cancer.',
      uploadInstructions:
        'Please upload one (1) H&E slide image with the tumor that has the highest Gleason grade used by the pathologist in making his or her diagnosis for the patient.',
      order: 1,
      inputLabel: 'Date of Biopsy',
    },
    [SpecimenType.ProstatePostRp]: {
      displayName: 'Prostate — Post-RP',
      description: `Select this option when submitting a surgical specimen following a radical prostatectomy (RP).
        The intended use population for the Post-RP test are patients who have had a biochemical recurrence (BCR) following RP,
        but have not yet had hormone therapy or radiation therapy. Patients who show nodal involvement or metastatic disease
        at the time of BCR or have had neoadjuvant therapy for their RP are not the intended use population for this test.`,
      uploadInstructions:
        'Please upload one (1) H&E slide image with the tumor that has the highest Gleason grade used by the pathologist in making his or her diagnosis for the patient.',
      order: 2,
      inputLabel: 'Date of RP',
    },
    [SpecimenType.MetastaticProstateBiopsy]: {
      displayName: 'Prostate — mHSPC',
      description:
        'Select this option when submitting a specimen for patients with confirmed metastatic hormone-sensitive prostate cancer (mHSPC). This includes patients whose cancer has spread beyond the prostate to distant sites such as lymph nodes, bones, or other organs. This option is intended for advanced-stage disease and may include patients who are currently receiving or have previously received systemic therapies such as hormone therapy, chemotherapy, or targeted treatments.',
      uploadInstructions: 'N/A',
      order: 3,
      inputLabel: 'Date of Procedure',
    },
    [SpecimenType.BreastBiopsy]: {
      displayName: 'Breast',
      description:
        'Select this option when submitting a surgical tumor resection specimen for a patient with a confirmed diagnosis of breast cancer.',
      uploadInstructions:
        'Please upload one (1) H&E slide image of the primary tumor resection specimen used by the pathologist in making his or her diagnosis for the patient.',
      order: 4,
      inputLabel: 'Date of Procedure',
    },
    [SpecimenType.ColorectalCancer]: {
      displayName: 'Colorectal Cancer',
      description: 'N/A',
      uploadInstructions: 'N/A',
      order: 5,
      inputLabel: 'Date of Procedure',
    },
  };

  private static getEnabledSpecimenTypes(
    supportedWorkflowSpecimenTypes: Set<SpecimenType>,
  ): Set<SpecimenType> {
    const allTypes = Object.values(SpecimenType);
    return new Set(
      allTypes.filter((type) => supportedWorkflowSpecimenTypes.has(type)),
    );
  }

  public static hasMultipleSpecimenTypesEnabled(
    supportedWorkflowSpecimenTypes: Set<SpecimenType>,
  ): boolean {
    return (
      this.getEnabledSpecimenTypes(supportedWorkflowSpecimenTypes).size > 1
    );
  }

  public static getSpecimenTypeName(type: SpecimenType): string {
    const metadata = this.SPECIMEN_TYPE_METADATA[type];
    return metadata?.displayName || capitalizeFirstLetter(type);
  }

  public static getSpecimenTypeDescription(type: SpecimenType): string {
    const metadata = this.SPECIMEN_TYPE_METADATA[type];
    return metadata.description;
  }

  public static getSpecimenTypeInputLabel(type: SpecimenType): string {
    const metadata = this.SPECIMEN_TYPE_METADATA[type];
    return metadata.inputLabel;
  }

  public static getSpecimenTypeUploadInstructions(type: SpecimenType): string {
    const platform = getPlatform();
    switch (platform) {
      case DistributionPlatform.Ldt:
        return "Please refer to the pathology report to identify the patient's highest Gleason grade. Then, upload one (1) corresponding H&E slide image used for that diagnosis.";
      case DistributionPlatform.Clin:
      case DistributionPlatform.Samd: {
        const metadata = this.SPECIMEN_TYPE_METADATA[type];
        return metadata?.uploadInstructions || '';
      }
      default:
        throw new Error(`Unsupported platform: ${platform}`);
    }
  }

  public static getSpecimenTypeMetadata(type: SpecimenType) {
    return this.SPECIMEN_TYPE_METADATA[type];
  }

  public static getAllSpecimenTypeMetadata() {
    return this.SPECIMEN_TYPE_METADATA;
  }
}

export class EthnicityUtils {
  public static getEthnicityName(ethnicity: Ethnicity): string {
    switch (ethnicity) {
      case Ethnicity.HispanicOrLatino:
        return 'Hispanic or Latino';
      case Ethnicity.AmericanIndianOrAlaskaNative:
        return 'American Indian or Alaska Native';
      case Ethnicity.Asian:
        return 'Asian';
      case Ethnicity.BlackOrAfricanAmerican:
        return 'Black or African American';
      case Ethnicity.NativeHawaiianOrOtherPacificIslander:
        return 'Native Hawaiian or Other Pacific Islander';
      case Ethnicity.White:
        return 'White';
      case Ethnicity.Other:
        return 'Other';
      default:
        return capitalizeFirstLetter(ethnicity);
    }
  }
}

export class PatientStatusAtBiopsyUtils {
  public static getPatientStatusAtBiopsyName(
    status: PatientStatusAtBiopsy,
  ): string {
    switch (status) {
      case PatientStatusAtBiopsy.HospitalInpatient:
        return 'Hospital Inpatient';
      case PatientStatusAtBiopsy.HospitalOutpatient:
        return 'Hospital Outpatient';
      case PatientStatusAtBiopsy.NonHospitalOutpatient:
        return 'Non-Hospital Outpatient';
      case PatientStatusAtBiopsy.NotProvided:
        return 'Not Provided';
      default:
        return capitalizeFirstLetter(status);
    }
  }
}

export class PrimaryDiagnosisCodeUtils {
  public static getPrimaryDiagnosisCodeName(
    code: PrimaryDiagnosisCode,
  ): string {
    switch (code) {
      case PrimaryDiagnosisCode.C61:
        return 'C61';
      default:
        return capitalizeFirstLetter(code);
    }
  }
}

export class MetastaticDiagnosisCodeUtils {
  public static getMetastaticDiagnosisCodeName(
    code: MetastaticDiagnosisCode,
  ): string {
    switch (code) {
      case MetastaticDiagnosisCode.C79_51:
        return 'C79.51';
      default:
        return capitalizeFirstLetter(code);
    }
  }
}

export class TStageUtils {
  public static getTStageFieldLabel(specimenType: SpecimenType): string {
    return specimenType === SpecimenType.ProstatePostRp
      ? 'Pathologic T-stage'
      : 'Clinical T-stage';
  }

  public static getProstateBiopsyTStageName(status: TStage): string {
    switch (status) {
      case TStage.T1A:
        return 'T1a';
      case TStage.T1B:
        return 'T1b';
      case TStage.T1C:
        return 'T1c';
      case TStage.T2A:
        return 'T2a';
      case TStage.T2B:
        return 'T2b';
      case TStage.T2C:
        return 'T2c';
      case TStage.T3:
        return 'T3';
      case TStage.T3A:
        return 'T3a';
      case TStage.T3B:
        return 'T3b';
      case TStage.T4:
        return 'T4';
      case TStage.NotProvided:
        return 'Not Provided';
      default:
        return capitalizeFirstLetter(status);
    }
  }

  public static getProstatePostRpTStageName(status: TStage): string {
    switch (status) {
      case TStage.T2:
        return 'pT2';
      case TStage.T3A:
        return 'pT3a';
      case TStage.T3B:
        return 'pT3b';
      case TStage.T4:
        return 'pT4';
      case TStage.NotProvided:
        return 'Not Provided';
      default:
        return capitalizeFirstLetter(status);
    }
  }
}

export class SurgicalMarginUtils {
  public static getSurgicalMarginName(status: SurgicalMargin): string {
    switch (status) {
      case SurgicalMargin.Positive:
        return 'Positive';
      case SurgicalMargin.Negative:
        return 'Negative';
    }
  }
}

export class NStageUtils {
  public static getNStageName(status: NStage): string {
    switch (status) {
      case NStage.N0:
        return 'N0';
      case NStage.N1:
        return 'N1';
      case NStage.N1_2:
        return 'N1_2';
      case NStage.N2:
        return 'N2';
      case NStage.N2_3:
        return 'N2_3';
      case NStage.N3:
        return 'N3';
      default:
        return capitalizeFirstLetter(status);
    }
  }
}

export class NccnRiskCategoryUtils {
  public static getNccnRiskCategoryName(status: NccnRiskCategory): string {
    switch (status) {
      case NccnRiskCategory.Low:
        return 'Low';
      case NccnRiskCategory.VeryLow:
        return 'Very Low';
      case NccnRiskCategory.IntermediateFavorable:
        return 'Intermediate Favorable';
      case NccnRiskCategory.IntermediateUnfavorable:
        return 'Intermediate Unfavorable';
      case NccnRiskCategory.High:
        return 'High';
      case NccnRiskCategory.VeryHigh:
        return 'Very High';
      case NccnRiskCategory.NotProvided:
        return 'Not Provided';
      default:
        return capitalizeFirstLetter(status);
    }
  }

  public static validateNccnRiskRules = (
    nccnRiskCategory?: NccnRiskCategory | '',
    primaryGleasonScore?: string,
    secondaryGleasonScore?: string,
    baselinePsa?: number,
    tStage?: TStage | '',
  ): { isValid: boolean; errorMessage?: string } => {
    // Check if we have all the required values to perform validation
    if (
      !nccnRiskCategory ||
      nccnRiskCategory === NccnRiskCategory.NotProvided ||
      !primaryGleasonScore ||
      !secondaryGleasonScore ||
      baselinePsa == null ||
      !tStage ||
      tStage === TStage.NotProvided
    ) {
      return { isValid: true }; // Skip validation if not all fields are filled
    }

    const primaryScore = parseInt(primaryGleasonScore);
    const secondaryScore = parseInt(secondaryGleasonScore);
    const gleasonTotal = primaryScore + secondaryScore;
    const parsedPsa = baselinePsa;

    const tStageBetweenT1aAndT2a = [
      TStage.T1A,
      TStage.T1B,
      TStage.T1C,
      TStage.T2A,
    ].includes(tStage);

    const tStageBetweenT2bAndT4 = [
      TStage.T2B,
      TStage.T2C,
      TStage.T3A,
      TStage.T3B,
      TStage.T4,
    ].includes(tStage);

    const tStageBetweenT3AndT4 = [
      TStage.T3,
      TStage.T3A,
      TStage.T3B,
      TStage.T4,
    ].includes(tStage);

    const tStageBetweenT1aAndT2c = [
      TStage.T1A,
      TStage.T1B,
      TStage.T1C,
      TStage.T2A,
      TStage.T2B,
      TStage.T2C,
    ].includes(tStage);

    const nccnRiskCategoryIsLowRisk = [
      NccnRiskCategory.VeryLow,
      NccnRiskCategory.Low,
    ].includes(nccnRiskCategory);

    const nccnRiskCategoryIsIntermediateRisk = [
      NccnRiskCategory.IntermediateFavorable,
      NccnRiskCategory.IntermediateUnfavorable,
    ].includes(nccnRiskCategory);

    const nccnRiskCategoryIsHighRisk = [
      NccnRiskCategory.High,
      NccnRiskCategory.VeryHigh,
    ].includes(nccnRiskCategory);

    // Low risk validation logic
    if (nccnRiskCategoryIsLowRisk) {
      if (gleasonTotal === 6 && parsedPsa < 10 && tStageBetweenT2bAndT4) {
        return {
          isValid: false,
          errorMessage:
            'Patient with NCCN Very Low or Low should not have T-stage > T2a',
        };
      }

      if (gleasonTotal === 6 && parsedPsa >= 10 && tStageBetweenT1aAndT2a) {
        return {
          isValid: false,
          errorMessage:
            'Patient with NCCN Very Low or Low should not have PSA >= 10ng/mL',
        };
      }

      if (gleasonTotal === 6 && parsedPsa >= 10 && tStageBetweenT2bAndT4) {
        return {
          isValid: false,
          errorMessage:
            'Patient with NCCN Very Low or Low should not have PSA >= 10ng/mL & T-stage > T2a',
        };
      }

      if (gleasonTotal >= 7 && parsedPsa < 10 && tStageBetweenT1aAndT2a) {
        return {
          isValid: false,
          errorMessage:
            'Patient with NCCN Very Low or Low should not have Gleason total >=7',
        };
      }

      if (gleasonTotal >= 7 && parsedPsa < 10 && tStageBetweenT2bAndT4) {
        return {
          isValid: false,
          errorMessage:
            'Patient with NCCN Very Low or Low should not have Gleason total >=7 & T-stage > T2a',
        };
      }

      if (gleasonTotal >= 7 && parsedPsa >= 10 && tStageBetweenT1aAndT2a) {
        return {
          isValid: false,
          errorMessage:
            'Patient with NCCN Very Low or Low should not have Gleason total >=7 & PSA >= 10ng/mL',
        };
      }

      if (gleasonTotal >= 7 && parsedPsa >= 10 && tStageBetweenT2bAndT4) {
        return {
          isValid: false,
          errorMessage:
            'Patient with NCCN Very Low or Low should not have Gleason total >=7 & PSA >= 10ng/mL & T-stage > T2a',
        };
      }
    }

    // Intermediate risk validation logic
    if (nccnRiskCategoryIsIntermediateRisk) {
      if (gleasonTotal === 6 && parsedPsa < 10 && tStageBetweenT1aAndT2a) {
        return {
          isValid: false,
          errorMessage:
            'Patients with NCCN intermediate should not have all of the following: Primary Gleason Score + Secondary Gleason Score Gleason = 6, PSA < 10ng/mL, T-stage <=T2a',
        };
      }

      if (gleasonTotal <= 7 && parsedPsa <= 20 && tStageBetweenT3AndT4) {
        return {
          isValid: false,
          errorMessage:
            'Patient with NCCN Intermediate should not have T-stage >= T3',
        };
      }

      if (gleasonTotal <= 7 && parsedPsa > 20 && tStageBetweenT1aAndT2c) {
        return {
          isValid: false,
          errorMessage:
            'Patient with NCCN Intermediate should not have PSA > 20ng/mL',
        };
      }

      if (gleasonTotal <= 7 && parsedPsa > 20 && tStageBetweenT3AndT4) {
        return {
          isValid: false,
          errorMessage:
            'Patient with NCCN Intermediate should not have PSA > 20ng/mL & T-stage >= T3',
        };
      }

      if (gleasonTotal > 7 && parsedPsa <= 20 && tStageBetweenT3AndT4) {
        return {
          isValid: false,
          errorMessage:
            'Patient with NCCN Intermediate should not have Gleason total >7 & T-stage >= T3',
        };
      }

      if (gleasonTotal > 7 && parsedPsa <= 20 && tStageBetweenT1aAndT2c) {
        return {
          isValid: false,
          errorMessage:
            'Patient with NCCN Intermediate should not have Gleason total >7',
        };
      }

      if (gleasonTotal > 7 && parsedPsa > 20 && tStageBetweenT3AndT4) {
        return {
          isValid: false,
          errorMessage:
            'Patient with NCCN Intermediate should not have Gleason total >7 & PSA > 20ng/mL & T-stage >= T3',
        };
      }

      if (gleasonTotal > 7 && parsedPsa > 20 && tStageBetweenT1aAndT2c) {
        return {
          isValid: false,
          errorMessage:
            'Patient with NCCN Intermediate should not have Gleason total >7 & PSA > 20ng/mL',
        };
      }
    }

    // High risk validation logic
    if (nccnRiskCategoryIsHighRisk) {
      if (gleasonTotal <= 7 && parsedPsa <= 20 && tStageBetweenT1aAndT2c) {
        return {
          isValid: false,
          errorMessage:
            'Patient with NCCN High or Very High should have at least one of the following: Gleason total >7, PSA >20ng/mL, or T-stage >= T3',
        };
      }
    }

    return { isValid: true };
  };

  /**
   * Calculates NCCN risk category based on clinical parameters.
   *
   * @param primaryGleason - Primary Gleason score (1-5)
   * @param secondaryGleason - Secondary Gleason score (1-5)
   * @param baselinePsa - Baseline PSA value
   * @param tStage - T-Stage value
   * @param positiveCores - Number of positive cores
   * @param totalCores - Total number of cores
   * @returns Array of possible NCCN risk categories (typically one, but can be multiple for Low/Very Low)
   * @throws Error if unable to categorize with provided parameters
   */
  public static categorizeNccnRisk({
    primaryGleason,
    secondaryGleason,
    baselinePsa,
    tStage,
    positiveCores,
    totalCores,
  }: {
    primaryGleason: number;
    secondaryGleason: number;
    baselinePsa: number;
    tStage: TStage;
    positiveCores?: number;
    totalCores?: number;
  }): NccnRiskCategory[] {
    const sum = primaryGleason + secondaryGleason;
    const gradeGroupIs1 = sum <= 6;
    const gradeGroupIs1or2 = primaryGleason === 3 && sum <= 7;
    const gradeGroupIs2or3 = sum === 7;
    const gradeGroupIs3 = primaryGleason === 4 && secondaryGleason === 3;
    const gradeGroupMoreThan4 = sum >= 8;

    const pctCores =
      totalCores && positiveCores ? (positiveCores / totalCores) * 100 : 0;

    const psaLessThan10 = baselinePsa < 10;
    const psa10to20 = baselinePsa >= 10 && baselinePsa <= 20;
    const psaGreater20 = baselinePsa > 20;
    const psaGreater40 = baselinePsa > 40;

    // Stage ranges
    const tStageBetweenT1aAndT2a = [
      TStage.T1A,
      TStage.T1B,
      TStage.T1C,
      TStage.T2A,
    ].includes(tStage);

    const tStageBetweenT2bAndT2c = [TStage.T2B, TStage.T2C].includes(tStage);

    const tStageAtLeastT3 = [
      TStage.T3,
      TStage.T3A,
      TStage.T3B,
      TStage.T4,
    ].includes(tStage);

    const oneIRF = tStageBetweenT2bAndT2c || gradeGroupIs2or3 || psa10to20;
    const twoIRF =
      (tStageBetweenT2bAndT2c && gradeGroupIs2or3) ||
      (tStageBetweenT2bAndT2c && psa10to20) ||
      (gradeGroupIs2or3 && psa10to20);

    const oneHRF = tStageAtLeastT3 || gradeGroupMoreThan4 || psaGreater20;
    const twoHRF =
      (tStageAtLeastT3 && gradeGroupMoreThan4) ||
      (tStageAtLeastT3 && psaGreater40) ||
      (gradeGroupMoreThan4 && psaGreater40);

    const coresLessThanHalf = pctCores < 50.0;
    const coresAtLeastHalf = pctCores >= 50.0;

    // ----- Risk category rules -----

    // Low or Very Low
    if (tStageBetweenT1aAndT2a && gradeGroupIs1 && psaLessThan10) {
      // If the positive cores is less than 3, both low and very low are
      // possible but CS would default to low. When auto-selecting, we choose
      // low. However, if when validating, we should allow both.
      return [NccnRiskCategory.Low, NccnRiskCategory.VeryLow];
    }

    // Intermediate

    // Favorable Intermediate
    if (!oneHRF && !twoIRF && oneIRF && gradeGroupIs1or2 && coresLessThanHalf) {
      return [NccnRiskCategory.IntermediateFavorable];
    }

    // Unfavorable Intermediate
    if (!oneHRF && oneIRF && (twoIRF || gradeGroupIs3 || coresAtLeastHalf)) {
      return [NccnRiskCategory.IntermediateUnfavorable];
    }

    // High
    if (!twoHRF && oneHRF) {
      return [NccnRiskCategory.High];
    }

    // Very High
    if (twoHRF) {
      return [NccnRiskCategory.VeryHigh];
    }

    throw new Error('Unable to categorize NCCN risk with provided parameters.');
  }
}

export { notifySuccess, notifyError };

/**
 * Redirects to the given path and sets toast information in the session storage,
 * so the root layout can display the toast from a useEffect that reads and clears
 * the session storage toast information.
 */
export function redirectAndNotify(
  path: string,
  toastType: 'error' | 'success',
  toastMessage: string,
): never {
  window.sessionStorage.setItem('toastType', toastType);
  window.sessionStorage.setItem('toastMessage', toastMessage);

  throw redirect(path);
}

// Reusable role checking utilities
export function hasRole(
  userRoles: UserRole[],
  targetRoleName: UserRole,
): boolean {
  return userRoles.some((role) => role === targetRoleName);
}

// Convenience functions for role checking using the AuthenticationService
export function getUserRoles(): UserRole[] {
  return AuthenticationService.getInstance().getUserInfo().roles;
}

export function isCustomerSuccess(): boolean {
  const userRoles = getUserRoles();
  if (getPlatform() === DistributionPlatform.Ldt) {
    return hasRole(userRoles, UserRole.ArteraCustomerSuccess);
  }
  return false;
}

export function isSalesUser(): boolean {
  const userRoles = getUserRoles();
  if (getPlatform() === DistributionPlatform.Ldt) {
    return hasRole(userRoles, UserRole.ArteraSales);
  }
  return false;
}

export function isOrderingPhysician(): boolean {
  const userRoles = getUserRoles();
  if (getPlatform() === DistributionPlatform.Ldt) {
    return hasRole(userRoles, UserRole.OrderingPhysician);
  }
  return false;
}

export function isClinicAdmin(): boolean {
  const userRoles = getUserRoles();
  return hasRole(userRoles, UserRole.ClinicAdmin);
}

export function isClinicUser(): boolean {
  const userRoles = getUserRoles();
  return hasRole(userRoles, UserRole.ClinicUser);
}

export function isLabTech(): boolean {
  const userRoles = getUserRoles();
  return hasRole(userRoles, UserRole.LabTech);
}

export function isCustomerSuccessOrSalesUser(): boolean {
  return isCustomerSuccess() || isSalesUser();
}

export function canManageMembers(): boolean {
  if (getPlatform() === DistributionPlatform.Ldt) {
    return (
      isClinicAdmin() ||
      isOrderingPhysician() ||
      isCustomerSuccess() ||
      isSalesUser() ||
      isLabTech()
    );
  } else if (getPlatform() === DistributionPlatform.Samd) {
    return isClinicAdmin();
  }

  throw new Error(`Unsupported platform: ${getPlatform()}`);
}

export type ARTERA_ROLE_NAME =
  | 'clinic-admin'
  | 'clinic-user'
  | 'gc-clinic-admin'
  | 'gc-clinic-user'
  | 'ordering-physician'
  | 'artera-customer-success'
  | 'artera-sales'
  | 'post-rp-enabled';

export const getRoleDisplayName = (
  role: UserRole,
  platform: DistributionPlatform,
  region: Region,
): string => {
  if (platform === DistributionPlatform.Samd) {
    const roleNamesByRegion = {
      [Region.US]: {
        [UserRole.ClinicAdmin]: 'Admin',
        [UserRole.ClinicUser]: 'Member',
      },
      [Region.AU]: {
        [UserRole.ClinicAdmin]: 'Clinic Admin',
        [UserRole.ClinicUser]: 'Clinic User',
      },
      [Region.UK]: {
        [UserRole.ClinicAdmin]: 'Clinic Admin',
        [UserRole.ClinicUser]: 'Clinic User',
      },
      [Region.EU]: {
        [UserRole.ClinicAdmin]: 'Clinic Admin',
        [UserRole.ClinicUser]: 'Clinic User',
      },
      [Region.SG]: {
        [UserRole.ClinicAdmin]: 'Clinic Admin',
        [UserRole.ClinicUser]: 'Clinic User',
      },
    };

    return roleNamesByRegion[region][
      role as keyof (typeof roleNamesByRegion)[Region]
    ];
  } else if (platform === DistributionPlatform.Ldt) {
    switch (role) {
      case UserRole.ArteraCustomerSuccess:
        return 'Artera Customer Success';
      case UserRole.ArteraSales:
        return 'Artera Sales';
      case UserRole.OrderingPhysician:
        return 'Ordering Physician';
      case UserRole.PostRpEnabled:
        return 'Post RP Enabled';
      case UserRole.ClinicAdmin:
        return 'Clinic Admin';
      case UserRole.ClinicUser:
        return 'Clinic User';
      case UserRole.LabTech:
        return 'Lab Tech';
      default:
        return role;
    }
  }

  throw new Error(`Unsupported platform: ${platform}`);
};

/**
 * Get the description for a role. Auth0 doesn't allow us to set different descriptions for roles
 * based on application, so we need to use this function to get the description for a role.
 * @param role - The role
 * @param platform - The platform
 * @returns The description for the role
 */
export const getRoleDescription = (
  role: UserRole,
  platform: DistributionPlatform,
): string => {
  if (platform === DistributionPlatform.Samd) {
    switch (role) {
      case UserRole.ClinicAdmin:
        return 'A person with additional permissions to manage members.';
      case UserRole.ClinicUser:
        return 'A person who can view, create, edit, and submit orders, and additionally download approved test result reports.';
      default:
        throw new Error(`Unsupported role: ${role}`);
    }
  } else if (platform === DistributionPlatform.Ldt) {
    switch (role) {
      case UserRole.ArteraCustomerSuccess:
        return 'For Artera’s Customer Success users.';
      case UserRole.ArteraSales:
        return 'For Artera’s Sales users.';
      case UserRole.OrderingPhysician:
        return 'A licensed healthcare provider authorized to place and manage patient test orders on the Artera platform.';
      case UserRole.ClinicAdmin:
        return 'A clinical or non-clinical professional responsible for managing patient test orders on behalf of ordering physicians on the Artera platform.';
      case UserRole.LabTech:
        return 'A Lab Tech user who can upload slide images.';
      default:
        throw new Error(`Unsupported role: ${role}`);
    }
  }

  throw new Error(`Unsupported platform: ${platform}`);
};

/**
 * Get file extension from filename
 */
export const getFileExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf('.');
  return lastDotIndex !== -1 ? filename.slice(lastDotIndex).toLowerCase() : '';
};

/**
 * Calculate the remaining time until an invitation expires
 */
export const getInvitationValidTime = (expiresAt?: string | null): string => {
  if (!expiresAt) return 'N/A';

  try {
    const expiryDate = new Date(expiresAt);

    // Check if the date is invalid
    if (isNaN(expiryDate.getTime())) {
      return 'Invalid date';
    }

    const now = new Date();
    const timeDiff = expiryDate.getTime() - now.getTime();

    // If expired, return 'Time Out'
    if (timeDiff <= 0) {
      return 'Time Out';
    }

    const secondsRemaining = Math.floor(timeDiff / 1000);
    const minutesRemaining = Math.floor(secondsRemaining / 60);
    const hoursRemaining = Math.floor(minutesRemaining / 60);
    const daysRemaining = Math.floor(hoursRemaining / 24);

    if (daysRemaining > 0) {
      return daysRemaining === 1 ? '1 day' : `${daysRemaining} days`;
    }

    if (hoursRemaining > 0) {
      return hoursRemaining === 1 ? '1 hour' : `${hoursRemaining} hours`;
    }

    if (minutesRemaining > 0) {
      return minutesRemaining === 1
        ? '1 minute'
        : `${minutesRemaining} minutes`;
    }

    return 'Less than 1 minute';
  } catch {
    return 'Invalid date';
  }
};

/**
 * Zod schema for email validation.
 */
export const EMAIL_SCHEMA = z.email('Invalid email address');

/**
 * Validates if a string is a valid email address using Zod's email schema.
 * @param email - The email string to validate
 * @returns true if the email is valid, false otherwise
 */
export function isValidEmail(email: string): boolean {
  return EMAIL_SCHEMA.safeParse(email).success;
}
